import type { CSSProperties } from 'react'
import { Link, useParams } from 'react-router-dom'
import {
  api,
  type EventDetail,
  type Projection,
  type Match,
  type MatchHole,
  type PlayerScorecard,
  type ScorecardHole,
} from '../../api/public'
import { Empty, Failed } from '../../components/Async'
import { LiveLoading, LiveStatus } from '../../components/Live'
import { ScoreMark } from '../../components/ScoreMark'
import { withToPar } from '../../components/toPar'
import { nextPoll } from '../../components/eventState'
import { usePolling } from '../../hooks/usePolling'

/**
 * The round tracker: the view the garage's second screen opens and leaves open
 * (FR-053, FR-050c).
 *
 * # It replaces the spreadsheet's tracker, so it shows what that showed
 *
 * Each match, both players' hole-by-hole **gross and net**, the **strokes
 * received** that turn one into the other, the hole winners and the running
 * match status — updating as holes complete, with nobody touching it.
 *
 * That is more than the scoreboard carries on purpose. The scoreboard is read
 * on a phone between shots and shows the match; this is read across the room
 * and shows the golf. A hole is won on NET, and a net that appears without the
 * gross and the stroke beside it is a number the room cannot check.
 *
 * # A typed score is an ordinary score (FR-018b)
 *
 * When ingestion is down the commissioner types the holes in, and everything
 * here — the net, the stroke allocation, the hole winner, the running status —
 * works identically, because all of it is derived from the recorded card and
 * the record does not know how a score arrived. The only difference is a
 * visible mark saying it was typed (FR-015, FR-018e), which is a fact worth
 * seeing rather than a limitation.
 *
 * # Nothing on it is a control
 *
 * It is left unattended on a screen in a room full of people. There is nothing
 * to click.
 */
export function RoundTracker() {
  const { eventId } = useParams()
  const id = Number(eventId)
  // **It stops asking once the week is closed** (FR-050c). This is the view
  // that is opened and left open, which is exactly why it must: before this it
  // polled a finished round every ten seconds for as long as the tab was up --
  // twice over, because the low-net line below is its own loop.
  const live = usePolling(
    (s) => api.event(id, s),
    [id],
    (d: EventDetail) => nextPoll(d.state),
  )
  // **The low net comes from the server, not from these totals.**
  //
  // A second poll rather than a sum taken here, and the reason is the whole
  // hazard of a running scorecard: whoever has played FEWEST holes has the
  // lowest total, because they have simply added up less golf. The projection
  // route restricts the field to the holes EVERY card carries and reports which
  // (`through_holes`), which is the only honest way to name one — and it shares
  // that derivation with Complete Round, so this cannot disagree with what the
  // night eventually records.
  //
  // It is deliberately a SEPARATE load: if it fails, the tracker carries on
  // without the line rather than losing the scorecard with it.
  const projected = usePolling(
    (s) => api.projection(id, s),
    [id],
    (d: Projection) => nextPoll(d.event.state),
  )

  if (live.loading) return <LiveLoading what="the round" />
  if (!live.data) {
    return <Failed what="The round tracker" error={live.error ?? new Error('nothing loaded')} />
  }

  const detail: EventDetail = live.data
  const cards = new Map(detail.scorecards.map((c) => [c.player_id, c]))
  const holes = holeNumbers(detail)
  const paired = new Set(detail.matches.flatMap((m) => [m.player_a_id, m.player_b_id]))
  // **A completed event shows who PLAYED.**
  //
  // The scorecards are the event's participants, frozen at Start Round, and on
  // a knockout or a final most of them never tee off — the 2025-26 final has
  // five participants and two finalists. Rendering a card for each of the other
  // three fills half the screen with empty eighteen-column rows and labels them
  // a bye, which they are not: a bye sits out a match-play week's pairings and
  // still plays for the low net (FR-033), where these three were eliminated.
  //
  // While a round is still being played the opposite is true: a player with no
  // holes yet has simply not finished one, and belongs on the screen.
  const unpaired = detail.scorecards.filter(
    (c) => !paired.has(c.player_id) && (detail.state !== 'complete' || c.holes_assembled > 0),
  )

  // The low net is the second point of a MATCH-PLAY week (FR-036h). A major is
  // decided on placings across the whole field and a playoff on the match, so
  // neither has one to lead — and saying "nobody has finished a hole everybody
  // has finished" about a major is not a caveat, it is wrong.
  const lowNet = lowNetLeaders(projected.data, detail.event_type.format === 'match_play')

  return (
    /* `--rows` is how many grid rows will be stacked, which is what decides how
       tall each one may be on a second screen — see the fitted layout in
       styles.css. The component is the only thing that knows it.
       **Rows and not CARDS**: a stroke-play major is five single-player cards
       of three rows, a match week is three cards of seven, and counting cards
       makes the major look like the bigger page when it is the smaller one —
       shrinking its type to the floor while half the screen sits empty. */
    <section
      className="tracker"
      style={
        {
          '--rows': gridRows(detail, unpaired),
          '--cards': detail.matches.length + unpaired.length,
        } as CSSProperties
      }
      data-testid="round-tracker"
    >
      <h2>
        Week {detail.week_number} · {detail.course}
      </h2>
      <p className="note">
        <span className={`badge badge-${detail.state}`}>{detail.state.replace('_', ' ')}</span> ·{' '}
        {detail.tee} tee · {detail.holes_expected} holes ·{' '}
        {detail.each_plays_own_nine && <>each player on their own nine · </>}
        {detail.event_type.format === 'stroke_play' && <>stroke play, the whole field on net · </>}
        <Link to={`/events/${detail.id}`}>This week</Link> ·{' '}
        <Link to={`/events/${detail.id}/projection`}>Projection</Link>
      </p>

      <LiveStatus loadedAt={live.loadedAt} failures={live.failures} error={live.error} />

      <LowNetLine low={lowNet} />

      <HoleStrip holes={holes} card={courseCard(detail)} />

      {detail.matches.length === 0 && detail.scorecards.length === 0 ? (
        <Empty>No holes have been recorded yet. They appear here as they are played.</Empty>
      ) : null}

      {detail.matches.map((m) => (
        <MatchTracker
          key={m.id}
          match={m}
          holes={holes}
          a={cards.get(m.player_a_id)}
          b={cards.get(m.player_b_id)}
          lowNet={lowNet.players}
        />
      ))}

      {unpaired.map((card) => (
        <article
          key={card.player_id}
          className="tracker-match"
          aria-label={card.display_name}
          data-testid={`tracker-bye-${card.player_id}`}
        >
          {/* **The name is on the row, so the card carries no heading.** It
              said the name a second time and spent a line of a second screen
              doing it. A stroke-play card has nothing else to say -- the format
              is the same for all five and is stated once above -- so it has no
              head at all.

              **Two different reasons to have no match, said differently.** In a
              match-play week one player sits out the pairings and still plays
              for the low net (FR-033) -- that is a bye, and the card says so.
              In a stroke-play major NOBODY has a match, and telling all five
              players they have "no match this week" reads as five byes. */}
          {detail.event_type.format !== 'stroke_play' && (
            <div className="tracker-head">
              <p className="tracker-status">
                the bye · still plays, and still counts for the low net
              </p>
            </div>
          )}
          <div className="scroll-x">
            <table className="table tracker-table" style={holeColumns(holes)}>
              <caption className="visually-hidden">
                {card.display_name}, hole by hole. Hole numbers, par and stroke index are in the
                strip above.
              </caption>
              <TrackerCols holes={holes} />
              <tbody>
                <PlayerRows card={card} holes={holes} lowNet={lowNet.players} />
              </tbody>
            </table>
          </div>
        </article>
      ))}
    </section>
  )
}

/** One match: both cards, the hole winners, and where the match stands. */
function MatchTracker({
  match,
  holes,
  a,
  b,
  lowNet,
}: {
  match: Match
  holes: number[]
  a?: PlayerScorecard
  b?: PlayerScorecard
  lowNet: Set<string>
}) {
  const byHole = new Map(match.holes.map((h) => [h.hole, h]))
  const nameA = a?.display_name ?? match.player_a_id
  const nameB = b?.display_name ?? match.player_b_id

  return (
    <article
      className="tracker-match"
      aria-label={`${nameA} v ${nameB}`}
      data-testid={`tracker-match-${match.id}`}
    >
      {/* Both names are on the rows below, so the head carries only where the
          match stands. */}
      <div className="tracker-head">
        <p className="tracker-status" data-testid={`tracker-status-${match.id}`}>
          {runningStatus(match, nameA, nameB)}
          {match.outcome_note && <em className="mark mark-note"> — {match.outcome_note}</em>}
        </p>
      </div>

      <div className="scroll-x">
        <table className="table tracker-table" style={holeColumns(holes)}>
          <caption className="visually-hidden">
            {nameA} versus {nameB}, hole by hole. Hole numbers, par and stroke index are in the
            strip above.
          </caption>
          <TrackerCols holes={holes} />
          <tbody>
            {a && (
              <PlayerRows
                card={a}
                holes={holes}
                winner={(h) => byHole.get(h)}
                side="a"
                lowNet={lowNet}
              />
            )}
            {b && (
              <PlayerRows
                card={b}
                holes={holes}
                winner={(h) => byHole.get(h)}
                side="b"
                lowNet={lowNet}
              />
            )}
            <tr className="match-status-row">
              <th scope="row">Match</th>
              <td className="tracker-label" />
              {holes.map((n) => {
                const h = byHole.get(n)
                return (
                  <td key={n} data-testid={`tracker-after-${match.id}-${n}`}>
                    {h ? cellStatus(h, nameA, nameB) : '·'}
                  </td>
                )
              })}
              <td />
            </tr>
          </tbody>
        </table>
      </div>
    </article>
  )
}

/**
 * The column count, as a custom property the stylesheet computes a width from.
 *
 * The component is the only thing that knows how many holes the round has, and
 * the alternative — letting each table size to its own content — puts three
 * cards of the same round at three different widths, so hole 6 on one sits over
 * hole 7 on the next. On a screen read across a room that is worse than it
 * sounds.
 */
function holeColumns(holes: number[]): CSSProperties {
  return { '--hole-columns': holes.length } as CSSProperties
}

/** Who leads the low net, over which holes, and whether it is settled yet. */
interface LowNet {
  players: Set<string>
  names: string[]
  value: string | null
  /** It was level on net and went to the tie-break (FR-032). */
  tied: boolean
  through: number
  complete: boolean
  /** Why there is no leader yet, where the server said. */
  undetermined: string[]
  known: boolean
}

/**
 * The low net as the SERVER computed it.
 *
 * # Nothing here decides anything
 *
 * `result_type: "low_net"` names the player and the projection's
 * `through_holes` says over how many. Both come from the same derivation
 * Complete Round uses, so the line this feeds cannot disagree with the point
 * the night eventually awards — only with when.
 *
 * Summing the net column here instead would be a second implementation of a
 * scoring rule, and a wrong one: the totals on screen run over each player's
 * own holes, so the leader would be whoever has played least.
 */
function lowNetLeaders(projection: Projection | null, contested: boolean): LowNet {
  const empty: LowNet = {
    players: new Set(),
    names: [],
    value: null,
    tied: false,
    through: 0,
    complete: false,
    undetermined: [],
    known: false,
  }
  if (!projection) return empty

  const rows = projection.event.results.filter((r) => r.result_type === 'low_net')
  // No row and no low net to have one: this event does not award it. Saying
  // nothing is the honest rendering — the projection page carries what a major
  // or a playoff actually awards.
  if (rows.length === 0 && (!contested || projection.event.complete)) return empty

  return {
    players: new Set(rows.map((r) => r.player_id)),
    names: rows.map((r) => r.display_name),
    // A decimal string, shown as it arrived.
    value: rows[0]?.value ?? null,
    tied: rows.some((r) => r.tied),
    through: projection.event.through_holes.length,
    complete: projection.event.complete,
    undetermined: projection.event.undetermined,
    known: true,
  }
}

/**
 * The low net, stated with the holes it is computed over.
 *
 * # "Through 7" is not decoration
 *
 * It is what makes the claim true. A player four holes into a nine has the
 * lowest total on the page and is not leading anything, so a leader named
 * without the basis is worse than no leader at all — this says the basis every
 * time, and says so plainly when there is not one yet.
 */
function LowNetLine({ low }: { low: LowNet }) {
  // The projection has not loaded, or failed. The tracker is not diminished by
  // going without it, so it simply says nothing.
  if (!low.known) return null

  if (low.players.size === 0) {
    return (
      <p className="note low-net-line" data-testid="low-net-line">
        <span className="low-net-flag low-net-flag-none">low net</span>{' '}
        {low.undetermined.length > 0
          ? low.undetermined.join('; ')
          : 'nobody has finished a hole that everybody has finished, so there is nothing to ' +
            'compare the field on yet.'}
      </p>
    )
  }

  const who = low.names.join(' and ')
  return (
    <p className="note low-net-line" data-testid="low-net-line">
      <span className="low-net-flag">low net</span> <strong>{who}</strong>
      {low.value !== null && <> at {low.value}</>}
      {low.complete ? (
        <> — every card is in, so this is the round&rsquo;s low net.</>
      ) : (
        <>
          {' '}
          — through <strong>{low.through}</strong> {low.through === 1 ? 'hole' : 'holes'}, the{' '}
          {low.through === 1 ? 'one' : low.through} every player has finished. Anyone further round
          is not counted ahead for it, and the point is not awarded until everybody is in (FR-062).
        </>
      )}
      {low.tied && (
        <em className="mark">
          {' '}
          Level on net with another card and taken on the hardest holes — a match-play week&rsquo;s
          low net is never shared (FR-032).
        </em>
      )}
    </p>
  )
}

/**
 * The course, once, above every card.
 *
 * # The hole numbers are not a property of a match
 *
 * Every card on this page is the same nine holes of the same course, so
 * repeating the numbers, the par and the stroke index inside each one says the
 * same thing three times and spends three times the height saying it — height
 * that a second screen does not have to spare. They belong to the ROUND, so
 * they are stated once at the top and the cards below line up under them.
 *
 * The columns agree because every table on the page is laid out from the same
 * `<colgroup>` and the same column count, not because they happen to hold
 * similar content.
 *
 * # Par is here because a score means nothing without it
 *
 * A 5 is a birdie or a double depending on the hole, and the row that says
 * which was the one thing the tracker was missing.
 */
function HoleStrip({ holes, card }: { holes: number[]; card?: PlayerScorecard }) {
  const byHole = new Map((card?.holes ?? []).map((h) => [h.hole, h]))
  return (
    <div className="tracker-strip-card" data-testid="hole-strip">
      <div className="scroll-x">
        <table className="table tracker-table tracker-strip" style={holeColumns(holes)}>
          <TrackerCols holes={holes} />
          <thead>
            <tr>
              <th scope="col">Hole</th>
              {/* The label column of the cards below. Empty here rather than
                  headed, because what it holds is a row's name. */}
              <th scope="col" className="tracker-label" />
              {holes.map((n) => (
                <th key={n} scope="col">
                  {n}
                </th>
              ))}
              <th scope="col">Tot</th>
            </tr>
          </thead>
          <tbody>
            <tr className="tracker-par">
              <th scope="row">Par</th>
              <td className="tracker-label" />
              {holes.map((n) => (
                <td key={n}>{byHole.get(n)?.par ?? '·'}</td>
              ))}
              {/* The server's total, not a sum taken here. */}
              <td className="strong">{card?.par_total ?? ''}</td>
            </tr>
            <tr className="stroke-index-row">
              <th scope="row">SI</th>
              <td className="tracker-label" />
              {holes.map((n) => (
                <td key={n}>{byHole.get(n)?.stroke_index ?? '·'}</td>
              ))}
              <td />
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  )
}

/**
 * The columns, defined once per table and identically in all of them.
 *
 * The cards carry no header row of their own now, so their widths come from
 * here rather than from whatever the first row happens to hold — which is also
 * what keeps every card aligned with the strip above it.
 */
function TrackerCols({ holes }: { holes: number[] }) {
  return (
    <colgroup>
      <col className="tracker-col-name" />
      <col className="tracker-col-label" />
      {holes.map((n) => (
        <col key={n} />
      ))}
      <col className="tracker-col-total" />
    </colgroup>
  )
}

/**
 * Whichever card describes the course best.
 *
 * Par and the stroke index are the same for everybody — they are the course,
 * not the round — so any card carries them. The fullest one is taken so a
 * player who has recorded fewer holes cannot leave a gap in the strip.
 */
function courseCard(detail: EventDetail): PlayerScorecard | undefined {
  let best: PlayerScorecard | undefined
  for (const c of detail.scorecards) {
    if (!best || c.holes.length > best.holes.length) best = c
  }
  if (!best || !detail.each_plays_own_nine) return best
  // On the night each player played their own nine, no one card carries the
  // whole strip -- the front-nine cards describe holes 1-9 and the back-nine
  // cards 10-18 -- so the strip is laid from all of them. Its total is a nine's
  // par (the server's, and zero when the two nines differ), since nobody played
  // all eighteen.
  const holes = new Map<number, ScorecardHole>()
  for (const c of detail.scorecards) for (const h of c.holes) holes.set(h.hole, h)
  return {
    ...best,
    holes: [...holes.values()].sort((a, b) => a.hole - b.hole),
    par_total: detail.par,
  }
}

/**
 * One player's three rows: gross, the strokes they receive, and the net that
 * results.
 *
 * The stroke row is the reason the other two differ, and it is the row the
 * league's spreadsheet is consulted for most: whether this is a hole you get a
 * shot on. It is highlighted where a stroke falls and blank where none does —
 * "0" reads as a computed value, blank reads as nothing to receive.
 */
function PlayerRows({
  card,
  holes,
  winner,
  side,
  lowNet,
}: {
  card: PlayerScorecard
  holes: number[]
  winner?: (hole: number) => MatchHole | undefined
  side?: 'a' | 'b'
  lowNet: Set<string>
}) {
  const byHole = new Map(card.holes.map((h) => [h.hole, h]))
  return (
    <>
      <tr className="tracker-gross" data-testid={`tracker-gross-${card.player_id}`}>
        <th
          scope="row"
          rowSpan={3}
          className={`tracker-name${lowNet.has(card.player_id) ? ' is-low-net' : ''}`}
        >
          {card.display_name}
          {lowNet.has(card.player_id) && (
            <span className="low-net-flag" title="Leading the low net">
              low net
            </span>
          )}
        </th>
        <td className="tracker-label">gross</td>
        {holes.map((n) => {
          const h = byHole.get(n)
          return (
            <td
              key={n}
              className={grossClass(h)}
              data-testid={`tracker-hole-${card.player_id}-${n}`}
            >
              <ScoreMark score={h?.gross} par={h?.par} />
              {h && <ProvenanceMark hole={h} />}
            </td>
          )
        })}
        <td className="strong total-to-par">{withToPar(card.gross_total, card.gross_to_par)}</td>
      </tr>
      <tr className="tracker-strokes">
        {/* The row that explains why the other two differ. A player who
            receives none has a blank row, which is the honest rendering of
            nothing to receive -- "0" nine times reads as a computed result. */}
        <td className="tracker-label">strokes</td>
        {holes.map((n) => {
          const h = byHole.get(n)
          const gets = h ? h.strokes_allocated !== '0' : false
          return (
            <td key={n} className={gets ? 'receives' : 'no-stroke'}>
              {gets ? h?.strokes_allocated : '0'}
            </td>
          )
        })}
        <td className="allocated">{card.strokes_total}</td>
      </tr>
      <tr className="tracker-net">
        <td className="tracker-label">net</td>
        {holes.map((n) => {
          const h = byHole.get(n)
          const mh = winner?.(n)
          return (
            <td
              key={n}
              className={netClass(mh, side)}
              data-testid={`tracker-net-${card.player_id}-${n}`}
            >
              <ScoreMark score={h?.net} par={h?.par} />
            </td>
          )
        })}
        <td className="strong total-to-par">{withToPar(card.net_total, card.net_to_par)}</td>
      </tr>
    </>
  )
}

/**
 * Who won the hole, on NET — highlighted on the net row, because that is the
 * row it was decided on. A hole only one player has finished is awarded to
 * nobody (FR-035c).
 */
function netClass(h: MatchHole | undefined, side?: 'a' | 'b'): string {
  if (!h || !side) return 'tracker-net-cell'
  if (!h.contested) {
    return h.a_net !== null && h.b_net !== null ? 'hole-dead' : 'hole-pending'
  }
  return h.outcome === side ? 'hole-won' : h.outcome === 'halved' ? 'hole-halved' : 'hole-lost'
}

function grossClass(h?: ScorecardHole): string {
  if (!h) return 'tracker-unplayed'
  const parts: string[] = []
  if (h.provenance !== 'ingested') parts.push(`provenance-${h.provenance}`)
  if (h.flagged) parts.push('flagged')
  return parts.join(' ')
}

/** A typed or corrected score is marked wherever it appears (FR-015, FR-018e). */
function ProvenanceMark({ hole }: { hole: ScorecardHole }) {
  if (hole.provenance === 'ingested' && !hole.flagged && !hole.replayed) return null
  return (
    <span className="mark-group">
      {hole.provenance === 'manual' && (
        <abbr className="mark mark-manual" title="This score was typed in by hand">
          ✎
        </abbr>
      )}
      {hole.provenance === 'corrected' && (
        <abbr className="mark mark-corrected" title="This score was corrected">
          ✔
        </abbr>
      )}
      {hole.replayed && (
        <abbr className="mark mark-replayed" title="This hole was played more than once">
          ↺
        </abbr>
      )}
      {hole.flagged && (
        <abbr className="mark mark-flagged" title={hole.flag_reason ?? 'Flagged for review'}>
          !
        </abbr>
      )}
    </span>
  )
}

function cellStatus(h: MatchHole, a: string, b: string): string {
  if (!h.contested) return h.a_net !== null && h.b_net !== null ? 'over' : '·'
  if (!h.status_after || h.status_after === 'AS') return 'AS'
  const m = /^(\d+)UP (A|B)$/.exec(h.status_after)
  if (!m) return h.status_after
  return `${(m[2] === 'A' ? a : b).slice(0, 4)} ${m[1]}`
}

/** The match in one line, in golf's own phrasing. */
function runningStatus(match: Match, a: string, b: string): string {
  if (match.status === 'won' && match.winner_player_id) {
    const name = match.winner_player_id === match.player_a_id ? a : b
    return `${name} wins${match.margin ? ` ${match.margin}` : ''}`
  }
  if (match.status === 'halved') return 'halved'
  const decided = match.holes.filter((h) => h.contested)
  if (decided.length === 0) return 'not started'
  const last = decided[decided.length - 1]
  return cellStatusWords(last.status_after, a, b)
}

function cellStatusWords(status: string, a: string, b: string): string {
  if (!status || status === 'AS') return 'all square'
  const m = /^(\d+)UP (A|B)$/.exec(status)
  if (!m) return status
  return `${m[2] === 'A' ? a : b} ${m[1]} up`
}

/**
 * How many table rows the page will stack.
 *
 * Each player contributes three — gross, strokes, net — and each match one more
 * for its running status. This is what the fitted second-screen layout divides
 * the viewport by, so it has to match what is actually rendered rather than
 * approximate it: counting a player whose card has not arrived would shrink
 * every number on the screen for a row that is not there.
 */
function gridRows(detail: EventDetail, unpaired: PlayerScorecard[]): number {
  const recorded = new Set(detail.scorecards.map((c) => c.player_id))
  let rows = unpaired.length * 3
  for (const m of detail.matches) {
    if (recorded.has(m.player_a_id)) rows += 3
    if (recorded.has(m.player_b_id)) rows += 3
    rows += 1
  }
  return rows
}

/**
 * The holes the round covers.
 *
 * Taken from the MATCHES first, because the match engine is handed the event's
 * whole card — every hole it plays, recorded or not. A scorecard carries only
 * the holes actually recorded, so building the grid from those alone would make
 * the round shrink to whatever has been played and the columns shift under the
 * reader between updates.
 */
function holeNumbers(detail: EventDetail): number[] {
  const seen = new Set<number>()
  for (const m of detail.matches) for (const h of m.holes) seen.add(h.hole)
  for (const c of detail.scorecards) for (const h of c.holes) seen.add(h.hole)
  return [...seen].sort((x, y) => x - y)
}
